import { useState, useRef, useEffect } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronDown, GraduationCap } from 'lucide-react';
import { useCurrentBatch, ALL_BATCHES } from '@/lib/hooks/useCurrentBatch';
import { cn } from '@/lib/cn';

export function BatchSwitcher() {
  const { batch, batches, setBatch, isAll } = useCurrentBatch();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectBatch = (id: string) => {
    setBatch(id);
    setOpen(false);
    if (pathname.startsWith('/courses/') && id !== ALL_BATCHES) {
      navigate({ to: '/courses/$batchId', params: { batchId: id } });
    } else if (pathname.startsWith('/courses/') && id === ALL_BATCHES) {
      navigate({ to: '/dashboard' });
    }
  };

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  if (!batch && !isAll) return null;
  const single = batches.length <= 1;
  const label = isAll ? 'All Courses' : batch!.name;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={single}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-xs font-semibold disabled:cursor-default"
      >
        <GraduationCap className="h-3.5 w-3.5 text-primary" />
        {label}
        {!single && <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
      {open && (
        <div role="menu" className="absolute left-0 z-20 mt-1 min-w-[200px] overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
          {batches.length > 1 && (
            <button
              role="menuitem"
              onClick={() => selectBatch(ALL_BATCHES)}
              className={cn('flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted',
                isAll && 'font-semibold text-primary')}
            >
              All Courses
            </button>
          )}
          {batches.map((b) => (
            <button
              key={b.id}
              role="menuitem"
              onClick={() => selectBatch(b.id)}
              className={cn('flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted',
                !isAll && b.id === batch?.id && 'font-semibold text-primary')}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
